function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import { FrameQuery, Locale } from '@custom/schema';
import { useEffect, useRef, useState } from 'react';
import { useBreadcrumbs, useCurrentPath } from '../components/Routes/Menu';
import { isTruthy } from './isTruthy';
import { useLocale } from './locale';
import { useOperation } from './operation';
import { getCanonicalPath, useSelectedRegionObject } from './region';
/**
 * Generic push to dataLayer.
 */ export var pushDataLayerEvent = function(event) {
    // @todo remove console.log in production.
    console.log('push dataLayer event', event);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
};
/**
 * Tracks clicks on Download Center release files.
 */ export var trackDownloadCenterReleaseFile = function(hit) {
    var _hit_file_group, _hit_operating_system, _hit_release_number;
    var event = {
        event: 'click_download',
        event_category: 'engagement',
        file_name: hit.name,
        software_type: ((_hit_file_group = hit.file_group) === null || _hit_file_group === void 0 ? void 0 : _hit_file_group.join(', ')) || '',
        operating_system: ((_hit_operating_system = hit.operating_system) === null || _hit_operating_system === void 0 ? void 0 : _hit_operating_system.join(', ')) || '',
        release: ((_hit_release_number = hit.release_number) === null || _hit_release_number === void 0 ? void 0 : _hit_release_number.join(', ')) || ''
    };
    pushDataLayerEvent(event);
};
/**
 * Tracks the start of the Quote Tool form (first interaction).
 */ export var trackQuoteToolStart = function() {
    var event = {
        event: 'form_start',
        event_category: 'form',
        form_name: 'quote'
    };
    pushDataLayerEvent(event);
};
/**
 * Tracks Quote Tool step submissions with all relevant data.
 *
 * @param stepData - The data to track for the current step.
 */ export var trackQuoteToolStep = function(stepData) {
    var event = {
        event: 'form_submit',
        event_category: 'form',
        form_name: 'quote',
        step: stepData.step,
        region: stepData.region || '',
        product_type: stepData.productType || '',
        product_name: stepData.productName || '',
        product_id: stepData.productId || '',
        product_variation_name: stepData.productVariationName || '',
        product_variation_id: stepData.productVariationId || '',
        user_email: stepData.userEmail || '',
        options: stepData.options || {},
        options_only: stepData.optionsOnly || false
    };
    pushDataLayerEvent(event);
};
/**
 * Field name mapping from react-hook-form to legacy names for BC.
 */ var LEGACY_FIELD_NAME_MAP = {
    organization: 'organization',
    firstName: 'first_name',
    lastName: 'name',
    street: 'street',
    zip: 'zip',
    city: 'city',
    country: 'country',
    email: 'email_address',
    acceptPrivacy: 'data_protection_agreement'
};
/**
 * Maps field names to legacy names for BC.
 *
 * @param fieldName - The current field name.
 * @returns The legacy field name.
 */ var mapToLegacyFieldName = function(fieldName) {
    return LEGACY_FIELD_NAME_MAP[fieldName] || fieldName;
};
/**
 * Tracks Quote Tool form validation errors.
 *
 * @param errors - Object mapping field names to error state (true if error exists).
 */ export var trackQuoteToolError = function(errors) {
    // Map field names to legacy names for BC.
    var mappedErrors = {};
    Object.entries(errors).forEach(function(param) {
        var _param = _sliced_to_array(param, 2), field = _param[0], hasError = _param[1];
        var legacyFieldName = mapToLegacyFieldName(field);
        mappedErrors[legacyFieldName] = hasError;
    });
    var event = {
        event: 'form_error',
        event_category: 'form',
        form_name: 'quote',
        field_error: mappedErrors
    };
    pushDataLayerEvent(event);
};
/**
 * Hook to track Quote Tool step changes.
 * Tracks form_start on mount and form_submit when step changes.
 *
 * Note: Some steps are handled manually and excluded from automatic tracking:
 * - Step 3: Tracked manually in InstrumentSelection (product_type and product).
 * - Step 5: Tracked manually in the submit handler.
 *
 * @param currentStep - The current step number (1-5).
 * @param stepData - The data to track for the current step.
 */ export var useTrackQuoteToolStep = function(currentStep, stepData) {
    var _useState = _sliced_to_array(useState(false), 2), hasStarted = _useState[0], setHasStarted = _useState[1];
    var prevStepRef = useRef(currentStep);
    useEffect(function() {
        // Track form start only once on mount.
        if (!hasStarted) {
            trackQuoteToolStart();
            setHasStarted(true);
        }
    }, [
        hasStarted
    ]);
    useEffect(function() {
        // Only track when step actually changes (not just when stepData changes).
        var stepChanged = currentStep !== prevStepRef.current;
        prevStepRef.current = currentStep;
        // Track step submission when step changes (but not on initial mount).
        // Skip step 3 (product to options) - handled manually in InstrumentSelection.
        // Skip step 5 (success) - handled manually in the submit handler.
        if (hasStarted && stepChanged && currentStep > 1 && currentStep !== 3 && currentStep !== 5) {
            trackQuoteToolStep(stepData);
        }
    }, [
        currentStep,
        hasStarted,
        stepData
    ]);
};
export var useTrackRoute = function(page, options) {
    var _frameQuery_data;
    var path = useCurrentPath();
    var locale = useLocale();
    var breadcrumbs = useBreadcrumbs();
    var regionObject = useSelectedRegionObject();
    var frameQuery = useOperation(FrameQuery);
    // Track the last path we pushed an event for to prevent duplicates.
    var lastTrackedPathRef = useRef(null);
    // Track the path when this component first mounted (for string page validation).
    var initialPathRef = useRef(path);
    useEffect(function() {
        var _frameQuery_data;
        // Exit early if we are not in the browser context.
        if (typeof window === 'undefined') {
            return;
        }
        // Exit early if we already tracked this path.
        if (lastTrackedPathRef.current === path) {
            return;
        }
        // Exit early if page data is not yet available (prevents firing with stale data).
        if (page === undefined) {
            return;
        }
        // For string pages (e.g., 'News & Events'), only fire if still on the initial path.
        // This prevents firing when navigating away from this route component.
        if (typeof page === 'string' && path !== initialPathRef.current) {
            return;
        }
        // If page is a PageFragment, validate that it belongs to the current path.
        // This prevents firing with stale page data when navigating away from a Page route.
        // Skip this check when the caller explicitly opts out (e.g. HomePage, where the
        // Gatsby route /{region}/{locale}/ intentionally differs from the Drupal path alias).
        // Strip query params and normalise (removes trailing slash, region prefix) before comparing.
        if (!(options === null || options === void 0 ? void 0 : options.skipPathValidation) && typeof page !== 'string' && page.path) {
            var canonicalCurrentPath = getCanonicalPath(path.split('?')[0]);
            if (canonicalCurrentPath !== page.path) {
                return;
            }
        }
        var regionName = getEnglishRegionName(regionObject, (_frameQuery_data = frameQuery.data) === null || _frameQuery_data === void 0 ? void 0 : _frameQuery_data.regionTerms);
        var enPath = path;
        if (enPath && locale !== Locale.En) {
            enPath = path.replace("/".concat(locale, "/"), "/".concat(Locale.En, "/"));
        }
        var pageCategory = '';
        if (breadcrumbs.length > 1) {
            pageCategory = breadcrumbs.map(function(breadcrumb) {
                return processText(breadcrumb.title);
            }).join(' / ');
        }
        var pageType = page ? typeof page === 'string' ? processText(page) : processText(page.bundleLabel) : '';
        var event = {
            event: 'page_view',
            event_category: 'page_view',
            region: regionName,
            language: locale,
            deployment_version: getDeploymentVersion(),
            en_path: enPath,
            page_type: pageType,
            page_category: pageCategory
        };
        pushDataLayerEvent(event);
        // Mark this path as tracked.
        lastTrackedPathRef.current = path;
    }, [
        path,
        page,
        locale,
        breadcrumbs,
        regionObject,
        (_frameQuery_data = frameQuery.data) === null || _frameQuery_data === void 0 ? void 0 : _frameQuery_data.regionTerms,
        options === null || options === void 0 ? void 0 : options.skipPathValidation
    ]);
};
var getDeploymentVersion = function() {
    // Get the Netlify build ID.
    //https://docs.netlify.com/build/configure-builds/environment-variables/#build-metadata
    return process.env.BUILD_ID || '42';
};
/**
 * A simplified version of the processText function from DataLayer.php.
 * Processes text for GTM tracking by normalizing it.
 *
 * @param text - The text to process.
 * @returns The processed text.
 */ export var processText = function(text) {
    return text.replace(/&/g, 'and').toLowerCase()// Replace all remaining non-alphanumeric characters by a space.
    .replace(/[^a-zA-Z0-9_/[\] ]/g, ' ')// Keep only one space between words.
    .replace(/\s+/g, ' ').trim();
};
/**
 * Cleans text to create a CSS-safe identifier (similar to Drupal's Html::cleanCssIdentifier).
 *
 * @param text - The text to clean.
 * @returns A cleaned CSS-safe identifier.
 */ export var cleanIdentifier = function(text) {
    return text.toLowerCase().replace(/[^a-z0-9_-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
};
/**
 * Gets the English region name from region terms data.
 *
 * @param regionObject - The current region object.
 * @param regionTerms - All region terms from FrameQuery.
 * @returns The English region name (lowercased and processed).
 */ export var getEnglishRegionName = function(regionObject, regionTerms) {
    if (!regionObject || !regionTerms) {
        return 'switzerland';
    }
    // Get all region terms (not filtered by locale).
    var allRegions = regionTerms.filter(isTruthy);
    // Find the English version of the current region by matching termId.
    var englishRegion = allRegions.find(function(region) {
        return region.termId === regionObject.termId && region.locale === Locale.En;
    });
    if (englishRegion) {
        return processText(englishRegion.label);
    }
    return processText(regionObject.label);
};
/**
 * Gets the English label for a term (product type, etc.) from terms data.
 *
 * @param currentTerm - The current term object.
 * @param allTerms - All terms from the query.
 * @returns The English label (lowercased and processed).
 */ export var getEnglishTermLabel = function(currentTerm, allTerms) {
    if (!currentTerm || !allTerms) {
        return '';
    }
    // Get all terms (not filtered by locale).
    var terms = allTerms.filter(isTruthy);
    // Find the English version of the current term by matching termId.
    var englishTerm = terms.find(function(term) {
        return term.termId === currentTerm.termId && term.locale === Locale.En;
    });
    if (englishTerm) {
        return processText(englishTerm.label);
    }
    return processText(currentTerm.label);
};
